import { AxiosError, HttpStatusCode } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { BAR_CATEGORY_ID } from 'src/config-global';

import { ICategoryListItem } from 'src/types/category';
import { IInventoryBatch } from 'src/types/inventory-batch';
import { IBarHistory, IBarItem, IBarListItem } from 'src/types/bar';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  bar: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IBarListItem[];
  };
  item: IBarItem | null;
  transactions: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    transactions: {
      count: number;
      page: number;
      pageSize: number;
      pageCount: number;
      items: IBarHistory[];
    };
  };
  batches: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    items: IInventoryBatch[];
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  bar: {
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  item: null,
  transactions: {
    status: 'idle',
    error: undefined,
    transactions: {
      count: 0,
      page: 1,
      pageSize: 10,
      pageCount: 0,
      items: [],
    },
  },
  batches: {
    status: 'idle',
    error: undefined,
    items: [],
  },
};

const barSlice = createSlice({
  name: 'bar',
  initialState,
  reducers: {
    removeBarItem: (state, action) => {
      const { itemId } = action.payload;

      const index = state.bar.items.findIndex((item) => item._id === itemId);

      if (index > -1) {
        action.payload.index = index; // Store the index for rollback
        state.bar.items = state.bar.items.filter((item) => item._id !== itemId);
      }
    },
    rollbackBarItemDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.bar.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBarItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getBarItems.fulfilled, (state, action) => {
        state.status = 'success';
        state.bar = action.payload;
      })
      .addCase(getBarItems.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getBarItemById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getBarItemById.fulfilled, (state, action) => {
        state.status = 'success';
        state.item = action.payload;
      })
      .addCase(getBarItemById.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getBarTransactionsById.pending, (state) => {
        state.transactions.status = 'loading';
      })
      .addCase(getBarTransactionsById.fulfilled, (state, action) => {
        state.transactions.status = 'success';
        state.transactions.transactions = action.payload;
      })
      .addCase(getBarTransactionsById.rejected, (state, action) => {
        state.transactions.status = 'failure';
        state.transactions.error = action.error.message;
      })

      .addCase(getBatchesForBarItem.pending, (state) => {
        state.batches.status = 'loading';
      })
      .addCase(getBatchesForBarItem.fulfilled, (state, action) => {
        state.batches.status = 'success';
        state.batches.items = action.payload;
      })
      .addCase(getBatchesForBarItem.rejected, (state, action) => {
        state.batches.status = 'failure';
        state.batches.error = action.error.message;
      });
  },
});

export default barSlice.reducer;

export const { removeBarItem, rollbackBarItemDeletion } = barSlice.actions;

export const getBarItems = createAsyncThunk(
  'bar/getInventory',
  async (
    params: {
      page: number;
      limit: number;
      generalSearch?: string | null;
      itemName?: string | null;
      itemCode?: string | null;
      minQuantity?: number | null;
      maxQuantity?: number | null;
      minPrice?: number | null;
      maxPrice?: number | null;
      suppliers?: string[];
      supplierSeparator?: ',' | '|';
      units?: string[];
      unitsSeparator?: ',' | '|';
      alertSent?: string | null;
      availability?: string | null;
      alertEnabled?: boolean | null;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      suppliers: [],
      supplierSeparator: ',',
      units: [],
      unitsSeparator: ',',
      generalSearch: null,
      alertEnabled: null,
      itemName: null,
      itemCode: null,
      maxPrice: null,
      maxQuantity: null,
      minPrice: null,
      minQuantity: null,
      alertSent: null,
      availability: null,
      sort: 'desc',
      sortBy: 'updatedAt',
    }
  ) => {
    const {
      page,
      limit,
      generalSearch,
      itemName,
      itemCode,
      minQuantity,
      maxQuantity,
      minPrice,
      maxPrice,
      suppliers,
      supplierSeparator,
      units,
      unitsSeparator,
      alertEnabled,
      alertSent,
      availability,
      sortBy,
      sort,
    } = params;

    try {
      const response = await axios.get(
        `inventory/bar/item?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&itemName=${itemName ?? ''}&itemCode=${itemCode ?? ''}&minQuantity=${
          minQuantity ?? ''
        }&maxQuantity=${maxQuantity ?? ''}&minPrice=${minPrice ?? ''}&maxPrice=${
          maxPrice ?? ''
        }&suppliers=${
          suppliers ? suppliers.join(supplierSeparator) : ''
        }&categories=${BAR_CATEGORY_ID}&units=${
          units ? units.join(unitsSeparator) : ''
        }&alertEnabled=${alertEnabled ?? ''}&alertSent=${alertSent ?? ''}&availability=${
          availability ?? ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}&group=&createdBy=&status=active`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getBarItemById = createAsyncThunk(
  'bar/getInventoryById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`inventory/bar/item/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getBarTransactionsById = createAsyncThunk(
  'bar/transactions/byId',
  async (params: {
    itemID: string;
    page: number;
    limit: number;
    sort: 'asc' | 'desc';
    sortBy: string;
  }) => {
    try {
      const { itemID, page, limit, sort, sortBy } = params;
      const response = await axios.get(
        `inventory/bar/transaction/item/history/${itemID}?page=${
          page + 1
        }&limit=${limit}&sortBy=${sortBy}&sort=${sort}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getBatchesForBarItem = createAsyncThunk(
  'bar/batches/byId',
  async (params: { itemID: string; activeOnly?: boolean }) => {
    try {
      const { itemID, activeOnly = false } = params;
      const response = await axios.get(
        `inventory/item/${itemID}/batches?activeOnly=${activeOnly}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteBarItem = createAsyncThunk(
  'bar/deleteInventory',
  async (params: { item: IBarListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`inventory/bar/item/${item._id}`);
      return response.data.data;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.FailedDependency) {
        dispatch(rollbackBarItemDeletion({ item, index }));
        throw new AxiosError(err.response.data.message);
      }

      dispatch(rollbackBarItemDeletion({ item, index }));
      throw err;
    }
  }
);

export const disableBarItem = createAsyncThunk(
  'inventory/disableBarItem',
  async (params: { item: any; index: number }, { dispatch }) => {
    const { item, index } = params;
    try {
      const response = await axios.put(`inventory/bar/item/${item._id}`, {
        ...item,
        itemName: item.itemName,
        unit: item.unit._id,
        category: item.category.map((category: ICategoryListItem) => category._id),
        status: 'disabled',
      });
      return response.data.data;
    } catch (err) {
      dispatch(rollbackBarItemDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TCreateBarItem
  extends Omit<
    IBarItem,
    | '_id'
    | 'itemCode'
    | 'createdAt'
    | 'updatedAt'
    | 'unit'
    | 'category'
    | 'suppliers'
    | 'updatedBy'
    | 'availability'
    | 'group'
    | 'volume'
  > {
  category: string[];
  suppliers: string[];
  unit: string;
  group: string;
  volume: number;
}

export const createBarItem = createAsyncThunk(
  'bar/createBarItem',
  async (params: { newInventory: TCreateBarItem }) => {
    const { newInventory } = params;

    const response = await axios.post(`inventory/bar/item`, newInventory);
    return response.data.data;
  }
);

export const updateBarItem = createAsyncThunk(
  'inventory/updateInventory',
  async (params: { inventoryId: string; updatingInventory: TCreateBarItem }) => {
    const { updatingInventory, inventoryId } = params;
    const response = await axios.put(`inventory/bar/item/${inventoryId}`, updatingInventory);
    return response.data.data;
  }
);
